import type { ReportTypes } from '@netpurpose/api'
import type { UnboundedRange } from '#components/dataByMetric/FilterDrawer/RangeFilterCollapse'
import type { ChallengeProps } from '#components/feedback/feedbackTypes'
import type { DocumentName } from '#components/library/Documentation/documents'

// i.e. event_name in BigQuery
export enum AnalyticsEventName {
  AutoFileDownload = 'auto_file_download',
  ButtonClick = 'button_click',
  FormSubmit = 'form_submit',
  Hover = 'hover',
  InputClick = 'input_click',
  LinkClick = 'link_click',
  NoSearchResults = 'no_search_results',
  OptionSelect = 'option_select',
  PageView = 'page_view',
  TabSelect = 'tab_select',
}

export enum AnalyticsButtonName {
  CancelCompanyDataFiltersDrawer = 'cancel_company_data_filters_drawer',
  CancelDataByMetricFiltersDrawer = 'cancel_data_by_metric_filters_drawer',
  ClearCompanyDataFilters = 'clear_company_data_filters',
  ClearCompanyDataFiltersDrawer = 'clear_company_data_filters_drawer',
  ClearDataByMetricFilters = 'clear_data_by_metric_filters',
  ClearDataByMetricFiltersDrawer = 'clear_data_by_metric_filters_drawer',
  CompanyDashboardToggleNotApplicable = 'company_dashboard_toggle_not_applicable',
  CompanyDashboardToggleNotDisclosed = 'company_dashboard_toggle_not_disclosed',
  CompanySDGsViewByRevenue = 'company_sdgs_view_by_revenue',
  CompanySDGsViewBySDIRevenue = 'company_sdgs_view_by_sdi_revenue',
  CompareActivityDetail = 'compare_activity_detail',
  CompareAddFromPlaceholder = 'compare_add_from_placeholder',
  CompareAddItem = 'compare_add_item',
  CompareCompanyDetail = 'compare_company_detail',
  CompareCompanyList = 'compare_company_list',
  CompareDeleteItem = 'compare_delete_item',
  CompareDomainDetail = 'compare_domain_detail',
  CompareOpenColumnSelectDropdown = 'compare_open_column_select_dropdown',
  ComparePortfolioDetail = 'compare_portfolio_detail',
  ComparePortfolioList = 'compare_portfolio_list',
  CompareSnapshotsAddSelection = 'compare_snapshots_add_selection',
  CompareSnapshotsOpenModal = 'compare_snapshots_open_modal',
  CompareToggleNotApplicable = 'compare_toggle_not_applicable',
  CompareToggleNotDisclosed = 'compare_toggle_not_disclosed',
  CompareTriggerSearch = 'compare_trigger_search',
  CompareViewByEVIC = 'compare_view_by_evic',
  CompareViewByRevenue = 'compare_view_by_revenue',
  CreatePortfolio = 'create_portfolio',
  CreatePortfolioDirectHoldings = 'create_portfolio_direct_holdings',
  CreatePortfolioFundOfFunds = 'create_portfolio_fof',
  CreatePortfolioOpenModal = 'create_portfolio_open_modal',
  CreatePortfolioSnapshotOpenModal = 'create_portfolio_snapshot_open_modal',
  CreateSnapshotDirectHoldings = 'create_snapshot_direct_holdings',
  CreateSnapshotFundOfFunds = 'create_snapshot_fof',
  CreateWatchlist = 'create_watchlist',
  DownloadDataDictionary = 'download_data_dictionary',
  DownloadEntityReport = 'download_entity_report',
  DownloadPortfolio = 'download_portfolio',
  DownloadPortfolioPAIs = 'download_portfolio_PAIs',
  DownloadPortfolioSDGRevenue = 'download_portfolio_sdg_revenue',
  DownloadPortfolioSDGs = 'download_portfolio_SDGs',
  DownloadPortfolioTemplateDirect = 'download_portfolio_template_direct_holdings',
  DownloadPortfolioTemplateFundOfFunds = 'download_portfolio_template_fof',
  DownloadSDIDataBundle = 'download_sdi_data_bundle',
  DownloadSDISubscriberPack = 'download_sdi_subscriber_pack',
  EditPortfolio = 'edit_portfolio',
  ExpandFactChangeHistoryAccordion = 'expand_fact_change_history_accordion',
  ExploreCompaniesSDGRevenueRangeFilterApply = 'explore_companies_sdg_revenue_range_filter_apply',
  ExploreCompaniesSDGRevenueRangeFilterOpen = 'explore_companies_sdg_revenue_range_filter_open',
  ExportCompare = 'export_compare',
  ExportDataDictionary = 'export_data_dictionary',
  ExportEntityDashboard = 'export_entity_dashboard',
  ExportEntityReport = 'export_entity_report',
  ExportEntitySDGRevenueAlignmentExcel = 'export_entity_sdg_revenue_alignment_excel',
  ExportEntitySDIRevenueExcel = 'export_entity_sdi_revenue_excel',
  ExportHistoryDownload = 'export_history_download',
  ExportHistoryDropdownDownload = 'export_history_dropdown_download',
  ExportHistoryDropdownRetry = 'export_history_dropdown_retry',
  ExportHistoryRetry = 'export_history_retry',
  ExportPortfolio = 'export_portfolio',
  ExportPortfolioDashboardExcel = 'export_portfolio_dashboard_excel',
  ExportPortfolioDataExcel = 'export_portfolio_data_excel',
  ExportPortfolioHoldingsExcel = 'export_portfolio_holdings_excel',
  ExportPortfolioPAIs = 'export_portfolio_PAIs',
  ExportPortfolioSDGRevenue = 'export_portfolio_sdg_revenue',
  ExportPortfolioSDGs = 'export_portfolio_SDGs',
  GetAccessEstimationDetail = 'get_access_estimation_detail',
  GetAccessResearch = 'get_access_research',
  GlobalSearchAskAiFromButton = 'global_search_ask_ai_from_button',
  GlobalSearchAskAiFromKeyboardShortcut = 'global_search_ask_ai_from_keyboard_shortcut',
  MetricConfigCancel = 'metric_config_cancel',
  MetricConfigCancelDelete = 'metric_config_cancel_delete',
  MetricConfigConfirmDelete = 'metric_config_confirm_delete',
  MetricConfigSaveChanges = 'metric_config_save_changes',
  MetricConfigToggleIncludeEstimated = 'metric_config_toggle_include_estimated',
  MetricConfigToggleNotApplicable = 'metric_config_toggle_not_applicable',
  NavBarGetAPIKey = 'nav_bar_get_api_key',
  OpenCompanyDataFilters = 'open_company_data_filters',
  OpenDataByMetricFilters = 'open_data_by_metric_filters',
  OpenGlobalSearchFromKeyboardShortcut = 'open_global_search_from_keyboard_shortcut',
  OpenGlobalSearchFromNavBar = 'open_global_search_from_nav_bar',
  PortfolioDashboardToggleNotApplicable = 'portfolio_dashboard_toggle_not_applicable',
  PortfolioDashboardToggleNotDisclosed = 'portfolio_dashboard_toggle_not_disclosed',
  PortfolioHoldingsSDGRevenueRangeFilterApply = 'portfolio_holdings_sdg_revenue_range_filter_apply',
  PortfolioHoldingsSDGRevenueRangeFilterOpen = 'portfolio_holdings_sdg_revenue_range_filter_open',
  PortfolioSDGsHideZeroPercent = 'portfolio_sdgs_hide_zero_percent',
  PortfolioSDGsShowZeroPercent = 'portfolio_sdgs_show_zero_percent',
  PortfolioSDGsTableMenuIcon = 'portfolio_sdgs_table_menu_icon',
  PortfolioSDGsViewByAUM = 'portfolio_sdgs_view_by_aum',
  PortfolioSDGsViewByHoldings = 'portfolio_sdgs_view_by_holdings',
  PortfolioSDGsViewByOutcomes = 'portfolio_sdgs_view_by_outcomes',
  PortfolioSDGsViewByRevenue = 'portfolio_sdgs_view_by_revenue',
  PortfolioSDGsViewBySDILabels = 'portfolio_sdgs_view_by_sdi_labels',
  PortfolioSDGsViewBySDIRevenue = 'portfolio_sdgs_view_by_sdi_revenue',
  PortfolioSnapshotCancelDelete = 'portfolio_snapshot_cancel_delete',
  PortfolioSnapshotConfirmDelete = 'portfolio_snapshot_confirm_delete',
  ReTryExportEntityReport = 're_try_export_entity_report',
  ReTryExportPortfolio = 're_try_export_portfolio',
  SDGSeeResearch = 'sdg_see_research',
  ViewEstimations = 'view_estimations',
}

export enum AnalyticsLinkName {
  NavBarViewCompare = 'nav_bar_view_compare',
  NavBarViewLibrary = 'nav_bar_view_library',
  NavBarViewWhatsNew = 'nav_bar_view_whats_new',
  OpenFactViewerForYoYVarianceReason = 'open_fact_viewer_for_yoy_variance_reason',
  OpenFactViewerFromKpiResultHover = 'open_fact_viewer_from_kpi_result_hover',
  OpenFactViewerFromReportedDataHover = 'open_fact_viewer_from_reported_data_hover',
  OpenFactViewerFromSdiRevenueHover = 'open_fact_viewer_from_sdi_revenue_hover',
  OpenFileFromLibraryDocumentation = 'open_file_from_library_documentation',
  PortfolioDashboardWithSDGFilterApplied = 'portfolio_dashboard_with_sdg_filter_applied',
  PortfolioDataForSDGRevenueMetric = 'portfolio_data_for_sdg_revenue_metric',
  PortfolioDataForSDIRevenueMetric = 'portfolio_data_for_sdi_revenue_metric',
  ToActivitiesPageFromDomainDetail = 'to_activities_page_from_domain_detail',
  ToEditMetricConfigFromCompany = 'to_edit_metric_config_from_company',
  ToEditMetricConfigFromCompanyData = 'to_edit_metric_config_from_company_data',
  ToEditMetricConfigFromCompare = 'to_edit_metric_config_from_compare',
  ToEditMetricConfigFromMetricConfigList = 'to_edit_metric_config_from_metric_config_list',
  ToEditMetricConfigFromNavBarMenu = 'to_edit_metric_config_from_nav_bar_menu',
  ToEditMetricConfigFromPortfolio = 'to_edit_metric_config_from_portfolio',
  ToEditMetricConfigFromPortfolioDisclosure = 'to_edit_metric_config_from_portfolio_disclosure',
  ToEntityDetailFromDomainDetail = 'to_entity_detail_from_domain_detail',
  ToMetricConfig = 'to_metric_config',
  ToMetricConfigFromMetricConfigList = 'to_metric_config_from_metric_config_list',
  ToMetricConfigListFromCompany = 'to_metric_config_list_from_company',
  ToMetricConfigListFromCompanyData = 'to_metric_config_list_from_company_data',
  ToMetricConfigListFromCompare = 'to_metric_config_list_from_compare',
  ToMetricConfigListFromNavBarMenu = 'to_metric_config_list_from_nav_bar_menu',
  ToMetricConfigListFromPortfolio = 'to_metric_config_list_from_portfolio',
  ToMetricConfigListFromPortfolioDisclosure = 'to_metric_config_list_from_portfolio_disclosure',
  ToNewMetricConfigFromCompany = 'to_new_metric_config_from_company',
  ToNewMetricConfigFromCompanyData = 'to_new_metric_config_from_company_data',
  ToNewMetricConfigFromCompare = 'to_new_metric_config_from_compare',
  ToNewMetricConfigFromMetricConfigList = 'to_new_metric_config_from_metric_config_list',
  ToNewMetricConfigFromNavBarMenu = 'to_new_metric_config_from_nav_bar_menu',
  ToNewMetricConfigFromPortfolio = 'to_new_metric_config_from_portfolio',
  ToNewMetricConfigFromPortfolioDisclosure = 'to_new_metric_config_from_portfolio_disclosure',
  ViewCompanySDGsRevenueMethodology = 'view_company_sdgs_revenue_methodology',
  ViewEstimationsFromEstimationDerivationHover = 'view_estimations_from_estimation_derivation_hover',
  ViewEstimationsWriteUp = 'view_estimations_write_up',
  ViewFactFromDataChangeLog = 'view_fact_from_data_change_log',
  ViewPortfolioSDGsOutcomesMethodology = 'view_portfolio_sdgs_methodology',
  ViewPortfolioSDGsRevenueMethodology = 'view_portfolio_sdgs_revenue_methodology',
}

export enum AnalyticsFormName {
  AddCompaniesToWatchlist = 'add_companies_to_watchlist',
  CompanyDataFilters = 'company_data_filters',
  EditPortfolio = 'edit_portfolio',
  ReportValue = 'report_value',
}

export enum AnalyticsOptionSelectName {
  DataByMetricSelectMetric = 'data_by_metric_select_metric',
  DataByMetricSelectTheme = 'data_by_metric_select_theme',
  GlobalSearchSelectAiNavResult = 'global_search_select_ai_nav_result',
  GlobalSearchSelectBaseline = 'global_search_select_baseline',
  GlobalSearchSelectCompany = 'global_search_select_company',
  GlobalSearchSelectDomain = 'global_search_select_domain',
  GlobalSearchSelectPortfolio = 'global_search_select_portfolio',
  GlobalSearchSelectQuestion = 'global_search_select_question',
  GlobalSearchSelectSolution = 'global_search_select_solution',
  MetricConfigSelectMetric = 'metric_config_select_metric',
  PortfolioDashboardSelectScope = 'portfolio_dashboard_select_scope',
  PortfolioDashboardSelectSDG = 'portfolio_dashboard_select_sdg',
  PortfolioDashboardSelectTheme = 'portfolio_dashboard_select_theme',
}

export enum AnalyticsDownloadFileName {
  Portfolio = 'portfolio',
  Unknown = 'unknown',
}

export enum AnalyticsInputName {
  ExportHistorySearchBar = 'export_history_search_bar',
  GlobalSearch = 'global_search',
}

// i.e. event_params in BigQuery
export type AnalyticsEventParams = {
  active_filters?: Record<string, (string | number)[] | UnboundedRange>
  button_name?: AnalyticsButtonName
  datapoint_id?: string
  entity_id?: number
  entity_ids?: number[]
  fact_id?: number
  file_name?: AnalyticsDownloadFileName | DocumentName
  form_name?: AnalyticsFormName
  has_viewed_latest_release?: boolean
  hover_item_name?: string
  input_name?: AnalyticsInputName
  is_editing_metric_config_name?: boolean
  is_new_metric_config?: boolean
  kpi_result_id?: string
  link_name?: AnalyticsLinkName
  message?: string
  metric_config_id?: string | null
  option_select_name?: AnalyticsOptionSelectName
  option_value?: string | number
  page_location?: string
  page_title?: string
  portfolio_id?: number | null
  // Google Analytics automatically converts this to a comma-separated string.
  portfolio_ids?: number[]
  question_id?: number | null
  question_name?: string
  report_value_id?: ChallengeProps['id']
  report_value_message?: string
  report_value_type?: ChallengeProps['type']
  sdg_major_code?: number
  search_term?: string
  snapshot_id?: number
  snapshot_ids?: number[]
  source_id?: number
  source_name?: string
  source_report_type?: ReportTypes | undefined
  source_url?: string
  tab_name?: string
  table_name?: string
  taxon_id?: number
  technology_id?: string
  theme?: string | null
  with_estimations?: boolean
}
